import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Button } from "semantic-ui-react";

import { PurProductStockRow } from "react-lib/apis/PUR/purStockApi";

import { Event as MeERPEvent, MeERPSlice, nextMeERPRequestKey } from "./MeERPInterface";

interface PurProductStockProps {
  onEvent: (event: MeERPEvent) => void;
  meerp: MeERPSlice;
  productId: number | null;
}
const PAGE_SIZE = 20;
// Same advisory window as the stock management screen's expiry remark.
const EXPIRING_MONTHS = 6;

const COLUMN_IDS = [
  "MEERP_STOCK_COL_TYPE", "MEERP_STOCK_COL_CODE", "MEERP_STOCK_COL_NAME", "MEERP_STOCK_COL_STORAGE",
  "MEERP_STOCK_COL_BIN", "MEERP_STOCK_COL_TOTAL", "MEERP_STOCK_COL_UNEXP", "MEERP_STOCK_COL_GRAND",
  "MEERP_STOCK_COL_MIN", "MEERP_STOCK_COL_MAX", "MEERP_STOCK_COL_REMARK", "MEERP_STOCK_COL_ACTIVE",
] as const;

// Stock aggregates belong to the shared core stock system; MeERP only reads.
const isBelowReorderPoint = (row: PurProductStockRow): boolean =>
  row.unexpQty !== null && row.minQty !== null && Number(row.unexpQty) < Number(row.minQty);

const isNearExpiry = (row: PurProductStockRow, now: Date): boolean => {
  if (row.minExpiry === null) return false;
  const expiry = new Date(row.minExpiry);
  if (Number.isNaN(expiry.getTime())) return false;
  const horizon = new Date(now);
  horizon.setMonth(horizon.getMonth() + EXPIRING_MONTHS);
  return expiry.getTime() <= horizon.getTime();
};

// Mirrors the stock management screen: active stock rows, or an active product.
const isActiveRow = (row: PurProductStockRow): boolean =>
  (row.activeCount !== null && row.activeCount > 0) ||
  (row.activeFlag !== null && row.activeFlag !== 0 && row.activeFlag !== 5);

const PurProductStock: React.FC<PurProductStockProps> = ({ onEvent, meerp, productId }) => {
  const intl = useIntl();
  const [request, setRequest] = useState<{ productId: number; offset: number; requestKey: number } | null>(null);

  // Selecting another medicine restarts at page one.
  useEffect(() => {
    if (productId === null) {
      setRequest(null);
      return;
    }
    setRequest({ productId, offset: 0, requestKey: nextMeERPRequestKey() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  useEffect(() => {
    if (!request || request.productId !== productId) return;
    onEvent({ message: "GetPurProductStock", params: { productId: request.productId, limit: PAGE_SIZE, offset: request.offset, requestKey: request.requestKey } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request, productId]);

  const stockState = meerp.stock;
  const stockCurrent = request !== null && request.productId === productId && stockState.requestKey === request.requestKey;
  const page = stockCurrent ? stockState.page : null;
  const loading = stockCurrent ? stockState.loading : productId !== null;
  const errorId = stockCurrent ? stockState.errorId : null;

  const quantity = (value: string | null): string => {
    if (value === null) return intl.formatMessage({ id: "MEERP_UNKNOWN" });
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed.toLocaleString("en-US") : intl.formatMessage({ id: "MEERP_UNKNOWN" });
  };
  const reload = () => {
    setRequest((previous) => previous && { productId: previous.productId, offset: 0, requestKey: nextMeERPRequestKey() });
  };

  if (productId === null) return null;

  return <section className="card-meerp__stock" aria-label={intl.formatMessage({ id: "MEERP_STOCK_SECTION" })}>
    <div className="card-meerp__stock-head">
      <h4>{intl.formatMessage({ id: "MEERP_STOCK_SECTION" })}</h4>
      <Button type="button" size="mini" disabled={loading || !request} onClick={reload}>{intl.formatMessage({ id: "MEERP_STOCK_RELOAD" })}</Button>
    </div>
    {loading && <small role="status">{intl.formatMessage({ id: "MEERP_LOADING" })}</small>}
    {errorId && <small role="alert" className="card-meerp__error-text">{intl.formatMessage({ id: errorId })}</small>}
    {page && !page.records.length && <small>{intl.formatMessage({ id: "MEERP_STOCK_EMPTY" })}</small>}
    {page && page.records.length > 0 && <>
      <div className="card-meerp__table-wrap"><table className="card-meerp__table">
        <caption className="card-meerp__sr-only">{intl.formatMessage({ id: "MEERP_STOCK_SECTION" })}</caption>
        <thead><tr>{COLUMN_IDS.map((id) => <th scope="col" key={id}>{intl.formatMessage({ id: id })}</th>)}</tr></thead>
        <tbody>{page.records.map((row, index) => {
          const remarks = [isBelowReorderPoint(row) ? intl.formatMessage({ id: "MEERP_STOCK_REMARK_LOW" }) : null, isNearExpiry(row, new Date()) ? intl.formatMessage({ id: "MEERP_STOCK_REMARK_EXPIRING" }) : null].filter(Boolean);
          return <tr key={`${row.storageId ?? "s"}-${row.productId}-${index}`}>
            <td>{row.productType || intl.formatMessage({ id: "MEERP_UNKNOWN" })}</td>
            <td>{row.code || intl.formatMessage({ id: "MEERP_UNKNOWN" })}</td>
            <td>{row.name || intl.formatMessage({ id: "MEERP_UNKNOWN" })}</td>
            <td>{row.storageName || (row.storageId ? `#${row.storageId}` : intl.formatMessage({ id: "MEERP_UNKNOWN" }))}</td>
            <td>{row.binLocation || intl.formatMessage({ id: "MEERP_UNKNOWN" })}</td>
            <td>{quantity(row.totalQty)}</td>
            <td>{quantity(row.unexpQty)}</td>
            <td>{quantity(row.grandQty)}</td>
            <td>{quantity(row.minQty)}</td>
            <td>{quantity(row.maxQty)}</td>
            <td>{remarks.length ? remarks.join(" · ") : intl.formatMessage({ id: "MEERP_UNKNOWN" })}</td>
            <td aria-label={isActiveRow(row) ? intl.formatMessage({ id: "MEERP_STOCK_ACTIVE_ON" }) : intl.formatMessage({ id: "MEERP_STOCK_ACTIVE_OFF" })}>{isActiveRow(row) ? "✓" : intl.formatMessage({ id: "MEERP_UNKNOWN" })}</td>
          </tr>;
        })}</tbody>
      </table></div>
      <small>{intl.formatMessage({ id: "MEERP_STOCK_NOTE" })}</small>
      {request && <div className="card-meerp__lookup-pager">
        <Button type="button" size="mini" disabled={loading || request.offset === 0} onClick={() => setRequest((previous) => previous && { ...previous, offset: Math.max(0, previous.offset - PAGE_SIZE), requestKey: nextMeERPRequestKey() })}>{intl.formatMessage({ id: "MEERP_PREVIOUS" })}</Button>
        <small>{intl.formatMessage({ id: "MEERP_PAGE" }, { current: Math.floor(request.offset / PAGE_SIZE) + 1, total: Math.max(1, Math.ceil(page.count / PAGE_SIZE)) })}</small>
        <Button type="button" size="mini" disabled={loading || request.offset + PAGE_SIZE >= page.count} onClick={() => setRequest((previous) => previous && { ...previous, offset: previous.offset + PAGE_SIZE, requestKey: nextMeERPRequestKey() })}>{intl.formatMessage({ id: "MEERP_NEXT" })}</Button>
      </div>}
    </>}
  </section>;
};

export default PurProductStock;
